import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TaskSchedule } from '../../types/task';
import { FilterComponent } from '../shared/Filters/FilterComponent';

export interface TaskSchedulerProps {
  schedule: TaskSchedule;
  onChange: (schedule: TaskSchedule) => void;
  onValidate?: (scheduleType: 'cron' | 'rate', expression: string) => Promise<{ valid: boolean; nextRuns: Date[]; error?: string }>;
  className?: string;
}

const scheduleTypeOptions = [
  { label: 'Manual', value: 'manual' },
  { label: 'Cron Expression', value: 'cron' },
  { label: 'Rate Expression', value: 'rate' }
];

// AWS EventBridge Scheduler cron format: 6 fields (minute hour day-of-month
// month day-of-week year). Either day-of-month or day-of-week must be '?'
// (AWS requirement — both '*' is rejected). The backend wraps the value in
// cron(...) on save; users type just the inner expression.
const cronPresets = [
  { label: 'Every minute', value: '* * * * ? *' },
  { label: 'Every 5 minutes', value: '0/5 * * * ? *' },
  { label: 'Every 15 minutes', value: '0/15 * * * ? *' },
  { label: 'Every 30 minutes', value: '0/30 * * * ? *' },
  { label: 'Every hour', value: '0 * * * ? *' },
  { label: 'Every 2 hours', value: '0 0/2 * * ? *' },
  { label: 'Every 6 hours', value: '0 0/6 * * ? *' },
  { label: 'Every 12 hours', value: '0 0/12 * * ? *' },
  { label: 'Daily at midnight', value: '0 0 * * ? *' },
  { label: 'Daily at 6 AM', value: '0 6 * * ? *' },
  { label: 'Daily at 12 PM', value: '0 12 * * ? *' },
  { label: 'Daily at 6 PM', value: '0 18 * * ? *' },
  { label: 'Weekly on Monday', value: '0 0 ? * MON *' },
  { label: 'Monthly on 1st', value: '0 0 1 * ? *' }
];

// AWS EventBridge Scheduler rate format: "<value> <unit>" (minutes|hours|days).
// The backend wraps the value in rate(...) on save; users type just the inner
// expression.
const ratePresets = [
  { label: 'Every 1 minute', value: '1 minute' },
  { label: 'Every 5 minutes', value: '5 minutes' },
  { label: 'Every 15 minutes', value: '15 minutes' },
  { label: 'Every 30 minutes', value: '30 minutes' },
  { label: 'Every 1 hour', value: '1 hour' },
  { label: 'Every 2 hours', value: '2 hours' },
  { label: 'Every 6 hours', value: '6 hours' },
  { label: 'Every 12 hours', value: '12 hours' },
  { label: 'Every 1 day', value: '1 day' },
  { label: 'Every 7 days', value: '7 days' }
];

const timezones = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Kolkata',
  'Australia/Sydney'
];

export function TaskScheduler({
  schedule,
  onChange,
  onValidate,
  className = ''
}: TaskSchedulerProps) {
  const [showPresets, setShowPresets] = useState(false);

  // THUB-298: hoisted above the effect that calls it and memoized, so the effect no longer
  // references it before declaration (react-hooks/immutability) and can list it as a real
  // dependency instead of suppressing exhaustive-deps.
  //
  // This replaces a note claiming validateExpression and onValidate were "intentionally
  // omitted" because including onValidate would re-run validation every parent render. That
  // risk is now handled by memoization rather than by omission: validateExpression only changes
  // when the expression, type or onValidate identity changes, so the effect runs when those do.
  // In practice the blast radius is zero today: the only caller is TaskCreator
  // (`<TaskScheduler schedule onChange />`) and it passes no onValidate, so this effect's body
  // is guarded off entirely. Worth re-checking if a caller ever starts passing one unmemoized.
  // THUB-298: validation is a QUERY keyed on what it validates, not a callback fired from an
  // effect that then setStates.
  //
  // The old pair was a useCallback plus an effect whose deps included the callback itself, so any
  // caller passing an unmemoized `onValidate` re-ran validation every render. The comment above
  // noted the blast radius was zero only because the sole caller passes no onValidate at all --
  // which is a statement about today's callers, not about the code being right.
  //
  // Keyed on (type, expression) means a given expression is validated once and the result is
  // reused; changing it is a different key. `enabled` reproduces the old guard exactly, including
  // returning no result for a manual schedule.
  const validationQuery = useQuery({
    queryKey: ['scheduleValidation', schedule.type, schedule.expression],
    queryFn: () => onValidate!(schedule.type as 'cron' | 'rate', schedule.expression!),
    enabled: !!onValidate && schedule.type !== 'manual' && !!schedule.expression,
    retry: false,
  });

  const isValidating = validationQuery.isFetching;
  interface ScheduleValidationResult {
    valid: boolean;
    nextRuns: Date[];
    error?: string;
  }
  const validationResult: ScheduleValidationResult | null = validationQuery.error
    ? { valid: false, nextRuns: [], error: 'Failed to validate expression' }
    : validationQuery.data ?? null;


  const handleScheduleChange = (field: keyof TaskSchedule, value: any) => {
    const newSchedule = {
      ...schedule,
      [field]: value
    };

    // Reset expression when changing type. No explicit result clearing needed: a different type
    // and a blank expression are a different query key, and the blank expression disables the
    // query, so there is nothing stale left to show.
    if (field === 'type' && value !== schedule.type) {
      newSchedule.expression = '';
    }

    onChange(newSchedule);
  };

  const handlePresetSelect = (preset: string) => {
    handleScheduleChange('expression', preset);
    setShowPresets(false);
  };

  const formatNextRun = (date: Date): string => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: schedule.timezone
    }).format(date);
  };

  const renderManualSchedule = () => (
    <div className="mb-5 bg-white dark:bg-neutral-900">
      <div className="
        bg-neutral-50 dark:bg-neutral-800
        border border-neutral-200 dark:border-neutral-700
        rounded-md p-5 text-center
      ">
        <p className="m-0 mb-2 text-neutral-600 dark:text-neutral-400 text-sm">
          This task will only run when manually triggered.
        </p>
        <p className="m-0 text-neutral-600 dark:text-neutral-400 text-sm">
          You can execute it from the task list or task details page.
        </p>
      </div>
    </div>
  );

  const renderCronSchedule = () => (
    <div className="mb-5 bg-white dark:bg-neutral-900">
      <div className="flex flex-col gap-5 bg-white dark:bg-neutral-900">
        <div className="relative bg-white dark:bg-neutral-900">
          <label htmlFor="cronExpression" className="
            block mb-1.5 text-sm font-medium text-neutral-800 dark:text-neutral-200
          ">
            Cron Expression
          </label>
          <div className="flex gap-2 relative bg-white dark:bg-neutral-900">
            <input
              id="cronExpression"
              type="text"
              value={schedule.expression}
              onChange={(e) => handleScheduleChange('expression', e.target.value)}
              placeholder="0 8 * * ? *"
              className={`
                flex-1 px-3 py-2.5 border rounded
                font-mono text-sm
                transition-colors duration-200
                bg-white dark:bg-neutral-900
                text-neutral-800 dark:text-neutral-200
                ${validationResult && !validationResult.valid
                  ? 'border-danger-500'
                  : 'border-neutral-300 dark:border-neutral-600 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20'
                }
                focus:outline-none
              `}
            />
            <button
              type="button"
              onClick={() => setShowPresets(!showPresets)}
              className="
                px-4 py-2.5
                bg-neutral-100 dark:bg-neutral-700
                border border-neutral-300 dark:border-neutral-600
                rounded text-sm cursor-pointer
                text-neutral-800 dark:text-neutral-200
                transition-all duration-200
                hover:bg-neutral-200 dark:hover:bg-neutral-600
                hover:border-neutral-400 dark:hover:border-neutral-500
              "
            >
              Presets
            </button>
          </div>
          {showPresets && (
            <div className="
              absolute top-full right-0 z-10 mt-1
              bg-white dark:bg-neutral-800
              border border-neutral-300 dark:border-neutral-600
              rounded shadow-lg
              max-h-[300px] overflow-y-auto min-w-[250px]
            ">
              {cronPresets.map((preset) => (
                <button
                  key={preset.value}
                  type="button"
                  onClick={() => handlePresetSelect(preset.value)}
                  className="
                    flex flex-col items-start w-full
                    px-4 py-3 border-0
                    bg-white dark:bg-neutral-800
                    cursor-pointer text-left
                    transition-colors duration-200
                    border-b border-neutral-100 dark:border-neutral-700
                    last:border-b-0
                    hover:bg-neutral-50 dark:hover:bg-neutral-700
                  "
                >
                  <span className="text-sm font-medium text-neutral-800 dark:text-neutral-200 mb-0.5">
                    {preset.label}
                  </span>
                  <span className="text-xs font-mono text-neutral-500 dark:text-neutral-400">
                    {preset.value}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="
          bg-neutral-50 dark:bg-neutral-800
          border border-neutral-200 dark:border-neutral-700
          rounded-md p-4
        ">
          <h4 className="m-0 mb-3 text-sm font-semibold text-neutral-800 dark:text-neutral-200">
            AWS Cron Expression Format:
          </h4>
          <div className="flex gap-2 mb-1 font-mono text-base font-bold text-primary-500">
            <span>*</span>
            <span>*</span>
            <span>*</span>
            <span>*</span>
            <span>?</span>
            <span>*</span>
          </div>
          <div className="flex gap-2 mb-3 text-[11px] uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
            <span className="min-w-[20px] text-center">min</span>
            <span className="min-w-[20px] text-center">hour</span>
            <span className="min-w-[20px] text-center">day</span>
            <span className="min-w-[20px] text-center">month</span>
            <span className="min-w-[20px] text-center">weekday</span>
            <span className="min-w-[20px] text-center">year</span>
          </div>
          <p className="m-0 mb-3 text-[12px] text-neutral-600 dark:text-neutral-400">
            Use <code className="bg-neutral-200 dark:bg-neutral-700 px-1 rounded font-mono text-xs">?</code> in either day-of-month or day-of-week (not both <code className="bg-neutral-200 dark:bg-neutral-700 px-1 rounded font-mono text-xs">*</code>). The <code className="bg-neutral-200 dark:bg-neutral-700 px-1 rounded font-mono text-xs">cron(...)</code> wrapper is added automatically on save.
          </p>
          <div className="mt-3">
            <p className="my-1 text-[13px] text-neutral-800 dark:text-neutral-200">
              <code className="bg-neutral-200 dark:bg-neutral-700 px-1.5 py-0.5 rounded font-mono text-xs text-neutral-800 dark:text-neutral-200">0 9 ? * MON-FRI *</code> - Every weekday at 9 AM
            </p>
            <p className="my-1 text-[13px] text-neutral-800 dark:text-neutral-200">
              <code className="bg-neutral-200 dark:bg-neutral-700 px-1.5 py-0.5 rounded font-mono text-xs text-neutral-800 dark:text-neutral-200">0/15 * * * ? *</code> - Every 15 minutes
            </p>
            <p className="my-1 text-[13px] text-neutral-800 dark:text-neutral-200">
              <code className="bg-neutral-200 dark:bg-neutral-700 px-1.5 py-0.5 rounded font-mono text-xs text-neutral-800 dark:text-neutral-200">0 0 1 * ? *</code> - First day of every month
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderRateSchedule = () => (
    <div className="mb-5 bg-white dark:bg-neutral-900">
      <div className="flex flex-col gap-5 bg-white dark:bg-neutral-900">
        <div className="relative bg-white dark:bg-neutral-900">
          <label htmlFor="rateExpression" className="
            block mb-1.5 text-sm font-medium text-neutral-800 dark:text-neutral-200
          ">
            Rate Expression
          </label>
          <div className="flex gap-2 relative bg-white dark:bg-neutral-900">
            <input
              id="rateExpression"
              type="text"
              value={schedule.expression}
              onChange={(e) => handleScheduleChange('expression', e.target.value)}
              placeholder="5 minutes"
              className={`
                flex-1 px-3 py-2.5 border rounded
                font-mono text-sm
                transition-colors duration-200
                bg-white dark:bg-neutral-900
                text-neutral-800 dark:text-neutral-200
                ${validationResult && !validationResult.valid
                  ? 'border-danger-500'
                  : 'border-neutral-300 dark:border-neutral-600 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20'
                }
                focus:outline-none
              `}
            />
            <button
              type="button"
              onClick={() => setShowPresets(!showPresets)}
              className="
                px-4 py-2.5
                bg-neutral-100 dark:bg-neutral-700
                border border-neutral-300 dark:border-neutral-600
                rounded text-sm cursor-pointer
                text-neutral-800 dark:text-neutral-200
                transition-all duration-200
                hover:bg-neutral-200 dark:hover:bg-neutral-600
                hover:border-neutral-400 dark:hover:border-neutral-500
              "
            >
              Presets
            </button>
          </div>
          {showPresets && (
            <div className="
              absolute top-full right-0 z-10 mt-1
              bg-white dark:bg-neutral-800
              border border-neutral-300 dark:border-neutral-600
              rounded shadow-lg
              max-h-[300px] overflow-y-auto min-w-[250px]
            ">
              {ratePresets.map((preset) => (
                <button
                  key={preset.value}
                  type="button"
                  onClick={() => handlePresetSelect(preset.value)}
                  className="
                    flex flex-col items-start w-full
                    px-4 py-3 border-0
                    bg-white dark:bg-neutral-800
                    cursor-pointer text-left
                    transition-colors duration-200
                    border-b border-neutral-100 dark:border-neutral-700
                    last:border-b-0
                    hover:bg-neutral-50 dark:hover:bg-neutral-700
                  "
                >
                  <span className="text-sm font-medium text-neutral-800 dark:text-neutral-200 mb-0.5">
                    {preset.label}
                  </span>
                  <span className="text-xs font-mono text-neutral-500 dark:text-neutral-400">
                    {preset.value}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="
          bg-neutral-50 dark:bg-neutral-800
          border border-neutral-200 dark:border-neutral-700
          rounded-md p-4
        ">
          <h4 className="m-0 mb-3 text-sm font-semibold text-neutral-800 dark:text-neutral-200">
            Rate Expression Format:
          </h4>
          <p className="my-2 text-[13px] text-neutral-800 dark:text-neutral-200">
            <code className="bg-neutral-200 dark:bg-neutral-700 px-1.5 py-0.5 rounded font-mono text-xs text-neutral-800 dark:text-neutral-200">value unit</code>
            <span className="ml-2 text-[12px] text-neutral-600 dark:text-neutral-400">The <code className="bg-neutral-200 dark:bg-neutral-700 px-1 rounded font-mono text-xs">rate(...)</code> wrapper is added automatically on save.</span>
          </p>
          <div className="mt-3">
            <p className="my-1 text-[13px] text-neutral-800 dark:text-neutral-200">
              <code className="bg-neutral-200 dark:bg-neutral-700 px-1.5 py-0.5 rounded font-mono text-xs text-neutral-800 dark:text-neutral-200">5 minutes</code> - Every 5 minutes
            </p>
            <p className="my-1 text-[13px] text-neutral-800 dark:text-neutral-200">
              <code className="bg-neutral-200 dark:bg-neutral-700 px-1.5 py-0.5 rounded font-mono text-xs text-neutral-800 dark:text-neutral-200">1 hour</code> - Every hour
            </p>
            <p className="my-1 text-[13px] text-neutral-800 dark:text-neutral-200">
              <code className="bg-neutral-200 dark:bg-neutral-700 px-1.5 py-0.5 rounded font-mono text-xs text-neutral-800 dark:text-neutral-200">7 days</code> - Every 7 days
            </p>
          </div>
          <p className="my-2 text-[13px] text-neutral-800 dark:text-neutral-200">
            <strong>Valid units:</strong> minute(s), hour(s), day(s)
          </p>
        </div>
      </div>
    </div>
  );

  const renderScheduleContent = () => {
    switch (schedule.type) {
      case 'manual':
        return renderManualSchedule();
      case 'cron':
        return renderCronSchedule();
      case 'rate':
        return renderRateSchedule();
      default:
        return renderManualSchedule();
    }
  };

  return (
    <div className={`
      bg-white dark:bg-neutral-900
      border border-neutral-200 dark:border-neutral-700
      rounded-lg p-5
      ${className}
    `}>
      <div className="
        grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-5 mb-6 items-end
        bg-white dark:bg-neutral-900
      ">
        <div className="min-w-[200px] bg-white dark:bg-neutral-900">
          <FilterComponent
            field="scheduleType"
            label="Schedule Type"
            type="select"
            options={scheduleTypeOptions}
            value={schedule.type}
            onChange={(filter) => handleScheduleChange('type', filter.value)}
            required
          />
        </div>

        {schedule.type !== 'manual' && (
          <div className="min-w-[200px] bg-white dark:bg-neutral-900">
            <FilterComponent
              field="timezone"
              label="Timezone"
              type="select"
              options={timezones.map(tz => ({ label: tz, value: tz }))}
              value={schedule.timezone}
              onChange={(filter) => handleScheduleChange('timezone', filter.value)}
              searchable
            />
          </div>
        )}

        <div className="flex items-center pt-5 bg-white dark:bg-neutral-900">
          <label className="
            flex items-center gap-2
            text-sm font-medium text-neutral-800 dark:text-neutral-200
            cursor-pointer
          ">
            <input
              type="checkbox"
              checked={schedule.enabled}
              onChange={(e) => handleScheduleChange('enabled', e.target.checked)}
              className="w-4 h-4 accent-primary-500"
            />
            Schedule enabled
          </label>
        </div>
      </div>

      {renderScheduleContent()}

      {schedule.type !== 'manual' && schedule.expression && (
        <div className="border-t border-neutral-200 dark:border-neutral-700 pt-5 bg-white dark:bg-neutral-900">
          {isValidating ? (
            <div className="flex items-center gap-2 text-neutral-500 dark:text-neutral-400 text-sm">
              <span>Validating expression...</span>
            </div>
          ) : validationResult ? (
            <div className={`
              rounded-md p-4
              ${validationResult.valid
                ? 'bg-success-50 dark:bg-success-900/20 border border-success-500'
                : 'bg-danger-50 dark:bg-danger-900/20 border border-danger-500'
              }
            `}>
              {validationResult.valid ? (
                <div>
                  <h4 className="m-0 mb-3 text-sm font-semibold text-success-600 dark:text-success-400">
                    ✓ Valid Expression
                  </h4>
                  {validationResult.nextRuns.length > 0 && (
                    <div>
                      <h5 className="m-0 mb-2 text-[13px] font-semibold text-success-600 dark:text-success-400">
                        Next 5 runs:
                      </h5>
                      <ul className="m-0 pl-5">
                        {validationResult.nextRuns.slice(0, 5).map((date, index) => (
                          <li key={index} className="text-xs font-mono text-neutral-800 dark:text-neutral-200 mb-1">
                            {formatNextRun(date)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <h4 className="m-0 mb-2 text-sm font-semibold text-danger-600 dark:text-danger-400">
                    ✗ Invalid Expression
                  </h4>
                  {validationResult.error && (
                    <p className="m-0 text-[13px] text-danger-600 dark:text-danger-400">
                      {validationResult.error}
                    </p>
                  )}
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}